import axios from 'axios';
import type {
  AksaraAdminProfileResponse,
  AksaraAdminTokenResponse,
} from '@/projects/aksara/models/adminAuth.model';
import type {
  AksaraAdminUserResponse,
  AksaraAdminUsersResponse,
  AksaraAdminUserSearchBy,
  AksaraAdminUserUpdate,
} from '@/projects/aksara/models/adminUser.model';
import type {
  AksaraAdminPostResponse,
  AksaraAdminPostStatusFilter,
  AksaraAdminPostsResponse,
  AksaraPostInteractionType,
  AksaraPostInteractionUsersResponse,
} from '@/projects/aksara/models/adminPost.model';
import type { AksaraAdminDashboard } from '@/projects/aksara/models/adminDashboard.model';

const aksaraAdminClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL_AKSARA,
  timeout: 30000,
});

export const aksaraAdminApiSource = {
  async dashboard(token: string): Promise<AksaraAdminDashboard> {
    const response = await aksaraAdminClient.get<{ data: AksaraAdminDashboard }>(
      '/admin/dashboard',
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },

  async login(googleToken: string): Promise<AksaraAdminTokenResponse> {
    const response = await aksaraAdminClient.post<{ data: AksaraAdminTokenResponse }>(
      '/admin/login',
      { token: googleToken },
    );
    return response.data.data;
  },

  async profile(token: string): Promise<AksaraAdminProfileResponse> {
    const response = await aksaraAdminClient.get<{ data: AksaraAdminProfileResponse }>(
      '/admin/profile',
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },

  async users(
    token: string,
    page: number,
    size: number,
    searchBy?: AksaraAdminUserSearchBy,
    query?: string,
  ): Promise<AksaraAdminUsersResponse> {
    const response = await aksaraAdminClient.get<{ data: AksaraAdminUsersResponse }>(
      '/admin/users',
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, size, ...(query?.trim() ? { searchBy, query: query.trim() } : {}) },
      },
    );
    return response.data.data;
  },
  async updateUser(token: string, id: string, payload: AksaraAdminUserUpdate) {
    const response = await aksaraAdminClient.put<{ data: AksaraAdminUserResponse }>(
      `/admin/users/${id}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
  async deleteUser(token: string, id: string): Promise<void> {
    await aksaraAdminClient.delete(`/admin/users/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  async posts(
    token: string,
    page: number,
    size: number,
    email?: string,
    status: AksaraAdminPostStatusFilter = 'ALL',
  ): Promise<AksaraAdminPostsResponse> {
    const response = await aksaraAdminClient.get<{ data: AksaraAdminPostsResponse }>(
      '/admin/posts',
      {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          size,
          ...(email?.trim() ? { email: email.trim() } : {}),
          ...(status === 'ALL' ? {} : { deleted: status === 'DELETED' }),
        },
      },
    );
    return response.data.data;
  },
  async postsByUser(
    token: string,
    userId: string,
    page: number,
    size: number,
  ): Promise<AksaraAdminPostsResponse> {
    const response = await aksaraAdminClient.get<{ data: AksaraAdminPostsResponse }>(
      `/admin/users/${userId}/posts`,
      { headers: { Authorization: `Bearer ${token}` }, params: { page, size } },
    );
    return response.data.data;
  },
  async post(token: string, id: string): Promise<AksaraAdminPostResponse> {
    const response = await aksaraAdminClient.get<{ data: AksaraAdminPostResponse }>(
      `/admin/posts/${id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data.data;
  },
  async postInteractionUsers(
    token: string,
    id: string,
    type: AksaraPostInteractionType,
    page: number,
    size: number,
  ): Promise<AksaraPostInteractionUsersResponse> {
    const response = await aksaraAdminClient.get<{ data: AksaraPostInteractionUsersResponse }>(
      `/admin/posts/${id}/${type}`,
      { headers: { Authorization: `Bearer ${token}` }, params: { page, size } },
    );
    return response.data.data;
  },
  async deletePost(token: string, id: string): Promise<void> {
    await aksaraAdminClient.delete(`/admin/posts/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  async updatePostDeleted(
    token: string,
    id: string,
    deleted: boolean,
  ): Promise<AksaraAdminPostResponse> {
    const response = await aksaraAdminClient.put<{ data: AksaraAdminPostResponse }>(
      `/admin/posts/${id}/deleted`,
      { deleted },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
};
